import { useQuery } from '@tanstack/react-query';
import { BarChart3, Calculator, Landmark, Settings2, Table2 } from 'lucide-react';
import type { ModuleNav } from '@/components/layout/module-nav';
import { useCapabilities } from '@/lib/auth/auth-context';
import { cashierHomeQuery } from './api';

/**
 * The submitted reports this user may accept now, for the count beside
 * Historia raportów. The home read every cash screen makes already carries it.
 */
const usePendingReports = (): number | null => {
  const { canAcceptCashReports } = useCapabilities();
  const home = useQuery({ ...cashierHomeQuery(), enabled: canAcceptCashReports });

  return home.data?.pendingReports || null;
};

/**
 * The cashier's navigation, one item per job, in the order of the day's work
 * and then looking back.
 *
 * Raport dnia is the cashier's; Historia raportów everyone's in the module —
 * a desk's month, where the desk manager accepts days, with a count of what
 * waits; Kasa główna the main cashier's, and management's who read its book:
 * the cash to accept and the payroll's cash pay are its drawers. Looking
 * back: Przegląd, every main desk's year. Settings are written with
 * `cashier.settings`, so the item follows `canEditCashierSettings` or a
 * read-only user.
 *
 * On a phone the items are the bottom tabs: the cash module is used standing
 * at a desk or in the office, phone in hand.
 */
export const CASHIER_NAV: ModuleNav = {
  bottomOnPhone: true,
  left: [
    [
      {
        to: '/cashier/close/{-$deskId}/{-$date}',
        labelKey: 'cashier.nav.close',
        shortLabelKey: 'cashier.nav.closeShort',
        icon: Calculator,
        capability: 'canReportCash',
      },
      {
        to: '/cashier/reports/{-$deskId}/{-$year}/{-$month}',
        labelKey: 'cashier.nav.reports',
        shortLabelKey: 'cashier.nav.reportsShort',
        icon: Table2,
        capability: ['canAcceptCashReports', 'canSeeCashier'],
        badge: usePendingReports,
      },
    ],
    [
      {
        to: '/cashier/main/{-$deskId}/{-$year}/{-$month}',
        labelKey: 'cashier.nav.main',
        icon: Landmark,
        capability: ['canKeepMainDesk', 'canSeeCashOverview'],
      },
      {
        to: '/cashier/overview/{-$year}',
        labelKey: 'cashier.nav.overview',
        icon: BarChart3,
        capability: 'canSeeCashOverview',
      },
    ],
  ],
  right: [
    {
      to: '/cashier/settings',
      labelKey: 'cashier.nav.settings',
      icon: Settings2,
      capability: 'canEditCashierSettings',
      readOnlyToo: true,
    },
  ],
};
